import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import DogCard from '../components/DogCard';
import './Home.css';

export default function Home() {
  const [featuredDogs, setFeaturedDogs] = useState([]);
  const [settings,     setSettings]     = useState({});
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    Promise.all([
      api.getDogs('featured=1'),
      api.getSettings(),
    ]).then(([dogs, s]) => {
      setFeaturedDogs(dogs.slice(0, 3));
      setSettings(s);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="home">
      {/* ── Hero ────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero__mountains" aria-hidden="true">
          <svg viewBox="0 0 1440 200" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,200 L0,140 L100,80 L200,120 L320,40 L440,100 L560,20 L680,90 L800,30 L920,95 L1040,50 L1160,110 L1280,60 L1440,100 L1440,200 Z" fill="rgba(181,149,106,0.2)"/>
            <path d="M0,200 L0,160 L160,100 L320,140 L480,80 L640,130 L800,70 L960,120 L1120,85 L1280,130 L1440,100 L1440,200 Z" fill="rgba(181,149,106,0.3)"/>
            <path d="M0,200 L0,180 L240,130 L480,165 L720,120 L960,158 L1200,130 L1440,155 L1440,200 Z" fill="rgba(181,149,106,0.4)"/>
          </svg>
        </div>
        <div className="hero__content container">
          <div className="hero__badge">🐾 New Mexico Rescue</div>
          <h1 className="hero__title">
            {settings.home_hero_text || 'Giving Rescued Dogs a Second Chance at Love'}
          </h1>
          <p className="hero__tagline">A Cause for Healing Paws</p>
          <div className="hero__actions">
            <Link to="/dogs" className="btn btn-primary">Meet Our Dogs</Link>
            <Link to="/donate" className="btn btn-secondary">Donate Today</Link>
          </div>
          <div className="hero__stats">
            <div className="hero__stat">
              <span className="hero__stat-icon">🏠</span>
              <span>Forever Homes Found</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-icon">🐕</span>
              <span>Dogs in Our Care</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-icon">❤️</span>
              <span>Volunteer-Run Rescue</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission strip ────────────────────────────────────── */}
      <section className="mission">
        <div className="container">
          <div className="mission__inner">
            <div className="mission__zia" aria-hidden="true">✦</div>
            <p>
              {settings.home_about_blurb || 'We are a volunteer-run rescue dedicated to finding forever homes for dogs in need across New Mexico.'}
            </p>
            <Link to="/about" className="btn btn-navy">Our Story</Link>
          </div>
        </div>
      </section>

      {/* ── Featured Dogs ─────────────────────────────────────── */}
      {featuredDogs.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="text-center" style={{ marginBottom: 48 }}>
              <h2 className="section-title">Dogs Looking for Homes</h2>
              <p className="section-subtitle">Each one has a story. Could you be their next chapter?</p>
            </div>
            <div className="home__dogs-grid">
              {featuredDogs.map(dog => (
                <DogCard key={dog.id} dog={dog} />
              ))}
            </div>
            <div className="text-center" style={{ marginTop: 40 }}>
              <Link to="/dogs" className="btn btn-primary">See All Available Dogs</Link>
            </div>
          </div>
        </section>
      )}

      {/* ── How to Adopt ──────────────────────────────────────── */}
      <section className="section how-section">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 48 }}>
            <h2 className="section-title">How Adoption Works</h2>
            <p className="section-subtitle">Three simple steps to bring a dog home</p>
          </div>
          <div className="how__steps">
            {[
              { n:'01', icon:'🔍', title:'Browse Dogs',    desc:'Explore our available dogs and find one that feels like a match for your family and lifestyle.' },
              { n:'02', icon:'📋', title:'Apply',          desc:'Fill out a simple adoption inquiry form. We review every application carefully and personally.' },
              { n:'03', icon:'🏠', title:'Welcome Home',   desc:'Once approved, your new best friend joins your family. We support you through the transition.' },
            ].map(step => (
              <div className="how__step" key={step.n}>
                <div className="how__step-num">{step.n}</div>
                <div className="how__step-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center" style={{ marginTop: 40 }}>
            <Link to="/dogs" className="btn btn-primary">Start Browsing</Link>
          </div>
        </div>
      </section>

      {/* ── Donate CTA ────────────────────────────────────────── */}
      <section className="donate-cta">
        <div className="donate-cta__mountains" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,80 L0,40 L180,10 L360,45 L540,5 L720,35 L900,0 L1080,30 L1260,10 L1440,35 L1440,80 Z" fill="var(--cream)"/>
          </svg>
        </div>
        <div className="container">
          <div className="donate-cta__inner">
            <h2>Your Support Saves Lives</h2>
            <p>Every donation goes directly toward veterinary care, food, and shelter for rescued dogs in New Mexico.</p>
            <Link to="/donate" className="btn btn-gold">Make a Donation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
